/**
 * English - `nav`: the dashboard's sidebar, header and search map.
 *
 * Keys: `headings.<snake>` for sidebar headings; `pages.<route>.label` for a
 * page, its route after `/app/` with `/` as `.` and `-` as `_` (`/app` is
 * `pages.dashboard`); `pages.<route>.sections.<snake>.label|description` for
 * the named things inside it that search offers; `layout.*` for the chrome and
 * `language.*` for the dashboard language menu. Search keywords stay English
 * in `layouts/navigation.tsx` and are not translated.
 */
const nav: Record<string, string> = {
  'headings.daily': 'Daily operations',
  'pages.dashboard.label': 'Home',
  'pages.dashboard.sections.running_low.label': 'Running low',
  'pages.dashboard.sections.running_low.description': 'The SKUs about to sell out',
  'pages.dashboard.sections.top_products.label': 'Top products',
  'pages.dashboard.sections.top_products.description': 'What is selling best right now',
  'pages.analytics.label': 'Analytics',
  'pages.analytics.sections.revenue_and_orders.label': 'Revenue and orders',
  'pages.analytics.sections.revenue_and_orders.description': 'What you sold over a period, and the average basket',
  'pages.analytics.sections.customers.label': 'Customers',
  'pages.analytics.sections.customers.description': 'How many are new, and how many came back',
  'pages.analytics.sections.top_products.label': 'Top products',
  'pages.analytics.sections.top_products.description': 'Your best sellers over the period',
  'pages.reports.label': 'Reports',
  'pages.reports.sections.sales_overview.label': 'Sales overview',
  'pages.reports.sections.sales_overview.description': 'Revenue, orders and the basket, with everything that moved them',
  'pages.reports.sections.orders.label': 'Orders',
  'pages.reports.sections.orders.description': 'Every order in the window, down to the record',
  'pages.reports.sections.customers.label': 'Customers',
  'pages.reports.sections.customers.description': 'Lifetime value, and how often they come back',
  'pages.reports.sections.payments_transactions.label': 'Payments & transactions',
  'pages.reports.sections.payments_transactions.description': 'What cleared, what failed, and what was refunded',
  'pages.reports.sections.returns_refunds_cancellations.label': 'Returns, refunds & cancellations',
  'pages.reports.sections.returns_refunds_cancellations.description': 'Everything that went backwards, and what it cost',
  'pages.reports.sections.tax_fees.label': 'Tax & fees',
  'pages.reports.sections.tax_fees.description': 'GST collected by rate and HSN, for the window you file on',
  'pages.orders.label': 'Orders',
  'pages.orders.sections.create_an_order.label': 'Create an order',
  'pages.orders.sections.create_an_order.description': 'Take an order by hand, for a phone or counter sale',
  'pages.orders.sections.move_an_order_along.label': 'Move an order along',
  'pages.orders.sections.move_an_order_along.description': 'Confirm, pack, ship, deliver or cancel',
  'pages.orders.sections.payment_and_totals.label': 'Payment and totals',
  'pages.orders.sections.payment_and_totals.description': 'What was charged, what is owed, and the tax on it',
  'pages.orders.sections.delivery_address.label': 'Delivery address',
  'pages.orders.sections.delivery_address.description': 'Where the order is going, and how it ships',
  'pages.order_requests.label': 'Returns & requests',
  'pages.order_policy.label': 'Returns & cancellations',
  'pages.store_locations.label': 'Store locations',
  'pages.our_team.label': 'Our team',
  'pages.services.label': 'Services',
  'pages.pricing_plans.label': 'Pricing plans',
  'pages.faqs.label': 'FAQs',
  'pages.careers.label': 'Careers',
  'pages.careers.sections.applications.label': 'Job applications',
  'pages.careers.sections.applications.description': 'People who applied for a role through your storefront',
  'pages.shipments.label': 'Shipments',
  'pages.shipments.sections.track_a_shipment.label': 'Track a shipment',
  'pages.shipments.sections.track_a_shipment.description': 'Where a parcel is, and what the courier last said',
  'pages.shipments.sections.returns.label': 'Returns',
  'pages.shipments.sections.returns.description': 'Parcels coming back to you',
  'pages.bookings.label': 'Bookings',
  'pages.bookings.sections.upcoming_and_today.label': 'Upcoming and today',
  'pages.bookings.sections.upcoming_and_today.description': 'The diary, by when it happens',
  'pages.bookings.sections.needs_a_reply.label': 'Needs a reply',
  'pages.bookings.sections.needs_a_reply.description': 'Requests still waiting on you to accept or decline',
  'pages.my_schedule.label': 'My schedule',
  'pages.invoices.label': 'Invoices',
  'pages.invoices.sections.export_invoices.label': 'Export invoices',
  'pages.invoices.sections.export_invoices.description': 'Download a period for your accountant',
  'pages.invoices.sections.taxable_value.label': 'Taxable value',
  'pages.invoices.sections.taxable_value.description': 'What was invoiced, and the tax on it',
  'pages.customers.label': 'Customers',
  'pages.customers.sections.import_customers.label': 'Import customers',
  'pages.customers.sections.import_customers.description': 'Bring a list in from a spreadsheet',
  'pages.customers.sections.tags_and_notes.label': 'Tags and notes',
  'pages.customers.sections.tags_and_notes.description': 'Label a customer, and keep private notes on them',
  'pages.customers.sections.email_subscription.label': 'Email subscription',
  'pages.customers.sections.email_subscription.description': 'Whether a customer agreed to marketing email',
  'pages.customers.sections.default_address.label': 'Default address',
  'pages.customers.sections.default_address.description': 'Where this customer usually wants things sent',
  'pages.catalog.label': 'Products',
  'pages.catalog.sections.import_products.label': 'Import products',
  'pages.catalog.sections.import_products.description': 'Bring your catalogue in from a spreadsheet',
  'pages.catalog.sections.pricing.label': 'Pricing',
  'pages.catalog.sections.pricing.description': 'Price, compare-at price and what it costs you',
  'pages.catalog.sections.variants.label': 'Variants',
  'pages.catalog.sections.variants.description': 'Sizes, colours and the options a product comes in',
  'pages.catalog.sections.media.label': 'Media',
  'pages.catalog.sections.media.description': 'Photos and video, and the order they appear in',
  'pages.catalog.sections.search_engine_listing.label': 'Search engine listing',
  'pages.catalog.sections.search_engine_listing.description': 'The title and description Google shows',
  'pages.catalog.sections.product_organisation.label': 'Product organisation',
  'pages.catalog.sections.product_organisation.description': 'Category, tags and how a product is filed',
  'pages.catalog.sections.metafields.label': 'Metafields',
  'pages.catalog.sections.metafields.description': 'Extra fields of your own on a product',
  'pages.reviews.label': 'Reviews',
  'pages.reviews.sections.pending.label': 'Pending reviews',
  'pages.reviews.sections.pending.description': 'Reviews waiting for your approval',
  'pages.reviews.sections.auto_publish.label': 'Auto-publish reviews',
  'pages.reviews.sections.auto_publish.description': 'Show new reviews straight away, or check them first',
  'pages.questions.label': 'Questions',
  'pages.questions.sections.pending.label': 'Unanswered questions',
  'pages.questions.sections.pending.description': 'Questions customers asked on your product pages',
  'pages.categories.label': 'Categories',
  'pages.categories.sections.new_category.label': 'New category',
  'pages.categories.sections.new_category.description': 'Add a group and choose where it sits',
  'pages.gift_cards.label': 'Gift cards',
  'pages.gift_cards.sections.create_a_gift_card.label': 'Create a gift card',
  'pages.gift_cards.sections.create_a_gift_card.description': 'Issue a card for an amount, with an optional expiry',
  'pages.gift_cards.sections.deactivate_a_card.label': 'Deactivate a card',
  'pages.gift_cards.sections.deactivate_a_card.description': 'Stop a card being spent',
  'pages.inventory.label': 'Inventory',
  'pages.inventory.sections.adjust_stock.label': 'Adjust stock',
  'pages.inventory.sections.adjust_stock.description': 'Correct a count, with a reason recorded',
  'pages.inventory.sections.reserved_and_available.label': 'Reserved and available',
  'pages.inventory.sections.reserved_and_available.description': 'What is held by open orders versus free to sell',
  'pages.inventory.sections.movements.label': 'Movements',
  'pages.inventory.sections.movements.description': 'Every change to a count, and who made it',
  'headings.marketing': 'Marketing',
  'pages.ai_images.label': 'AI Image Studio',
  'pages.ai_images.sections.creative_type.label': 'Creative type',
  'pages.ai_images.sections.creative_type.description': 'Product, banner, category, promotion, cart, social or hero',
  'pages.ai_images.sections.style.label': 'Style',
  'pages.ai_images.sections.style.description': 'Studio, lifestyle, premium and the rest',
  'pages.ai_images.sections.history.label': 'History',
  'pages.ai_images.sections.history.description': 'What you have generated, and what it cost',
  'pages.whatsapp.label': 'WhatsApp',
  'pages.whatsapp.sections.new_campaign.label': 'New campaign',
  'pages.whatsapp.sections.new_campaign.description': 'Send a message to a group of customers',
  'pages.discounts.label': 'Discounts',
  'pages.discounts.sections.discount_value.label': 'Discount value',
  'pages.discounts.sections.discount_value.description': 'A percentage, an amount off, or buy-one-get-one',
  'pages.discounts.sections.minimum_purchase.label': 'Minimum purchase',
  'pages.discounts.sections.minimum_purchase.description': 'What a cart must reach before the offer applies',
  'pages.discounts.sections.eligibility.label': 'Eligibility',
  'pages.discounts.sections.eligibility.description': 'Who the offer is for, and where it can be used',
  'pages.discounts.sections.active_dates.label': 'Active dates',
  'pages.discounts.sections.active_dates.description': 'When an offer starts and when it stops',
  'pages.discounts.sections.usage_limits.label': 'Usage limits',
  'pages.discounts.sections.usage_limits.description': 'How many times an offer may be redeemed',
  'pages.discounts.sections.combinations.label': 'Combinations',
  'pages.discounts.sections.combinations.description': 'Whether this offer stacks with your others',
  'headings.content': 'Store content',
  'pages.storefront.sections.themes.label': 'Themes',
  'pages.storefront.sections.themes.description': 'The design your shop uses, and the drafts beside it',
  'pages.storefront.sections.customise_the_theme.label': 'Customise the theme',
  'pages.storefront.sections.customise_the_theme.description': 'Sections, colours, fonts and the words on the page',
  'pages.storefront.sections.branding.label': 'Branding',
  'pages.storefront.sections.branding.description': 'Your logo, colours and the name shoppers see',
  'pages.storefront.sections.listing.label': 'Listing',
  'pages.storefront.sections.listing.description': 'How your shop appears when it is shared or searched',
  'pages.storefront.sections.announcement_bar.label': 'Announcement bar',
  'pages.storefront.sections.announcement_bar.description': 'The strip of text across the top of your shop',
  'pages.storefront.domain.label': 'Domains',
  'pages.storefront.domain.sections.connect_a_domain_you_own.label': 'Connect a domain you own',
  'pages.storefront.domain.sections.connect_a_domain_you_own.description': 'Point an address you already bought at your shop',
  'pages.storefront.domain.sections.find_a_new_domain.label': 'Find a new domain',
  'pages.storefront.domain.sections.find_a_new_domain.description': 'Search for an address and buy it',
  'pages.storefront.qr_code.label': 'QR code',
  'pages.storefront.qr_code.sections.download_the_code.label': 'Download the code',
  'pages.storefront.qr_code.sections.download_the_code.description': 'Save the QR image for a poster or a card',
  'pages.content.label': 'Content',
  'pages.content.pages.label': 'Pages',
  'pages.content.pages.sections.add_a_page.label': 'Add a page',
  'pages.content.pages.sections.add_a_page.description': 'Write a new page for your shop',
  'pages.content.blog.label': 'Blog',
  'pages.content.blog.sections.blogs_and_posts.label': 'Blogs and posts',
  'pages.content.blog.sections.blogs_and_posts.description': 'Your blogs, and the posts inside them',
  'pages.content.blog.sections.search_engine_listing.label': 'Search engine listing',
  'pages.content.blog.sections.search_engine_listing.description': 'The title and description Google shows for a post',
  'pages.content.metaobjects.label': 'Metaobjects',
  'pages.content.metaobjects.sections.definitions_and_fields.label': 'Definitions and fields',
  'pages.content.metaobjects.sections.definitions_and_fields.description': 'The shape of a custom record you can reuse',
  'pages.content.metaobjects.sections.entries.label': 'Entries',
  'pages.content.metaobjects.sections.entries.description': 'The records themselves, and what they point at',
  'pages.content.menus.label': 'Menus',
  'pages.content.menus.sections.menu_items.label': 'Menu items',
  'pages.content.menus.sections.menu_items.description': 'The links in a menu, and the order they sit in',
  'pages.content.metafields.label': 'Metafields',
  'pages.content.metafields.sections.add_a_definition.label': 'Add a definition',
  'pages.content.metafields.sections.add_a_definition.description': 'Create a custom field and say what it holds',
  'pages.content.translations.label': 'Translations',
  'pages.content.translations.sections.translate_with_ai.label': 'Translate with AI',
  'pages.content.translations.sections.translate_with_ai.description': 'Fill in missing translations for many items at once',
  'pages.payments.label': 'Payments',
  'pages.payments.sections.connect_razorpay.label': 'Connect Razorpay',
  'pages.payments.sections.connect_razorpay.description': 'Your key ID and secret, so you can take money online',
  'pages.payments.sections.webhook.label': 'Webhook',
  'pages.payments.sections.webhook.description': 'The callback Razorpay uses to confirm a payment',
  'pages.checkout_options.label': 'Checkout options',
  'pages.checkout_options.sections.cash_on_delivery.label': 'Cash on delivery',
  'pages.checkout_options.sections.cash_on_delivery.description': 'Whether shoppers may pay at the door, and the limits on it',
  'pages.checkout_options.sections.order_via_whatsapp.label': 'Order via WhatsApp',
  'pages.checkout_options.sections.order_via_whatsapp.description': 'Let shoppers send their cart as a message instead',
  'pages.checkout_options.sections.online_payment.label': 'Online payment',
  'pages.checkout_options.sections.online_payment.description': 'Whether checkout takes money through Razorpay',
  'pages.shipping.label': 'Shipping',
  'pages.shipping.sections.delivery_charge.label': 'Delivery charge',
  'pages.shipping.sections.delivery_charge.description': 'What you charge for delivery, and when it is free',
  'pages.shipping.sections.pickup_address.label': 'Pickup address',
  'pages.shipping.sections.pickup_address.description': 'Where couriers collect from',
  'pages.shipping.sections.courier_integration.label': 'Courier integration',
  'pages.shipping.sections.courier_integration.description': 'Book parcels with a courier from inside an order',
  'pages.shipping.sections.default_parcel.label': 'Default parcel',
  'pages.shipping.sections.default_parcel.description': 'The weight and size used when you do not say',
  'pages.shipping.sections.cash_on_delivery.label': 'Cash on delivery',
  'pages.shipping.sections.cash_on_delivery.description': 'Whether couriers may collect cash for you',
  'pages.email_templates.label': 'Email templates',
  'pages.email_templates.sections.change_the_wording.label': 'Change the wording',
  'pages.email_templates.sections.change_the_wording.description': 'Rewrite the subject and body of an email',
  'pages.email_templates.sections.restore_the_default.label': 'Restore the default',
  'pages.email_templates.sections.restore_the_default.description': 'Put an email back to how it started',
  'pages.team.label': 'Team members',
  'pages.team.sections.invite_a_team_member.label': 'Invite a team member',
  'pages.team.sections.invite_a_team_member.description': 'Add someone and choose what they may do',
  'pages.team.sections.roles_and_access.label': 'Roles and access',
  'pages.team.sections.roles_and_access.description': 'What each role is allowed to open and change',
  'pages.team.sections.deactivate_someone.label': 'Deactivate someone',
  'pages.team.sections.deactivate_someone.description': 'Stop an account signing in',
  'headings.settings': 'Store settings',
  'pages.setup_assistance.label': 'Setup assistance',
  'pages.setup_assistance.sections.request_help.label': 'Request help',
  'pages.setup_assistance.sections.request_help.description': 'Ask our team to set your shop up for you',
  'pages.setup_assistance.sections.hours_remaining.label': 'Hours remaining',
  'pages.setup_assistance.sections.hours_remaining.description': 'How much setup assistance your plan includes, and what is left',
  'pages.support_chat.label': 'Support',
  'pages.support_chat.sections.new_conversation.label': 'New conversation',
  'pages.support_chat.sections.new_conversation.description': 'Ask the NeonCarts team a question and get the answer in the same thread',
  'pages.billing.label': 'Billing',
  'pages.billing.sections.your_plan.label': 'Your plan',
  'pages.billing.sections.your_plan.description': 'What you are on, what it includes and what it costs',
  'pages.billing.sections.compare_plans.label': 'Compare plans',
  'pages.billing.sections.compare_plans.description': 'What you would get by moving up or down',
  'pages.billing.sections.auto_renew.label': 'Auto-renew',
  'pages.billing.sections.auto_renew.description': 'Whether the plan renews itself, and when it next charges',
  'pages.billing.sections.billing_history.label': 'Billing history',
  'pages.billing.sections.billing_history.description': 'What you have been charged, and what failed',
  'pages.billing.sections.usage.label': 'Usage',
  'pages.billing.sections.usage.description': 'How much of your plan you have used this cycle',
  'pages.billing.sections.trial.label': 'Trial',
  'pages.billing.sections.trial.description': 'How long your trial runs, and what happens after',
  'pages.settings.label': 'Settings',
  'pages.settings.sections.business_details.label': 'Business details',
  'pages.settings.sections.business_details.description': 'Your name, contact details, address and GSTIN',
  'pages.settings.sections.storefront_address.label': 'Storefront address',
  'pages.settings.sections.storefront_address.description': 'The handle your shop sits at',
  'pages.settings.sections.currency_and_timezone.label': 'Currency and timezone',
  'pages.settings.sections.currency_and_timezone.description': 'What you price in, and what clock you keep',
  'pages.security.label': 'Security',
  'pages.security.sections.change_your_password.label': 'Change your password',
  'pages.security.sections.change_your_password.description': 'Set a new password for your account',
  'pages.security.sections.signed_in_devices.label': 'Signed-in devices',
  'pages.security.sections.signed_in_devices.description': 'Everywhere your account is open, and how to end it',
  'pages.my_account.label': 'My Account',
  'pages.my_account.sections.profile.label': 'Your profile',
  'pages.my_account.sections.profile.description': 'Your name and phone number',
  'pages.my_account.sections.email.label': 'Change your email',
  'pages.my_account.sections.email.description': 'The address you sign in with',
  'pages.my_account.sections.preferences.label': 'Your preferences',
  'pages.my_account.sections.preferences.description': 'The language your dashboard is shown in',
  'layout.my_account': 'Open My Account',
  'pages.legal.label': 'Terms & Policies',
  'pages.legal.sections.accept.label': 'Accept updated policies',
  'pages.legal.sections.accept.description': 'The NeonCarts policies that apply to you, and whether you have accepted them',
  'pages.settings.languages.label': 'Languages',
  'pages.settings.languages.sections.default_language.label': 'Default language',
  'pages.settings.languages.sections.default_language.description': 'The languages your storefront offers, and the one it starts in',
  'pages.settings.languages.sections.dashboard_language.label': 'Dashboard language',
  'pages.settings.languages.sections.dashboard_language.description': 'The language this dashboard is shown in',
  'pages.settings.signature.label': 'Signature',
  'pages.settings.signature.sections.invoice.label': 'Invoice signature',
  'pages.settings.signature.sections.invoice.description': 'Your signature, name and designation on invoices',
  'pages.settings.signature.sections.email.label': 'Email signature',
  'pages.settings.signature.sections.email.description': 'The sign-off and contact details added to your emails',

  'layout.nav_label': 'Dashboard',
  'layout.your_business': 'Your business',
  'layout.your_business_lower': 'your business',
  'layout.expand_menu': 'Expand menu',
  'layout.collapse_menu': 'Collapse menu',
  'layout.open_menu': 'Open menu',
  'layout.close_menu': 'Close menu',
  'layout.expand_item': 'Expand {label}',
  'layout.collapse_item': 'Collapse {label}',
  'layout.pinned': 'Pinned',
  'layout.pin_item': 'Pin {label}',
  'layout.unpin_item': 'Unpin {label}',
  'layout.pins_full': 'You can pin up to {count} pages. Unpin one to make room.',
  'layout.expand_section': 'Show {label}',
  'layout.collapse_section': 'Hide {label}',
  'layout.move_pin_up': 'Move {label} up',
  'layout.move_pin_down': 'Move {label} down',
  'layout.pin_moved': '{label} moved to position {position} of {total}.',
  'layout.collapse_all_sections': 'Collapse all sections',
  'layout.expand_all_sections': 'Expand all sections',
  'layout.sign_out': 'Sign out',
  'layout.view_storefront': 'View storefront',
  'layout.business_email': 'Business Email',
  'layout.business_email_for': 'Business Email · {domain}',
  'layout.business_email_status': '{provider} · {status}',
  'layout.email_health.IN_PROGRESS': 'Setting up',
  'layout.email_health.HEALTHY': 'Active',
  'layout.email_health.NEEDS_ATTENTION': 'Needs attention',

  'language.button': 'Dashboard language: {language}',
  'language.menu': 'Dashboard language',

  // --- Sales channels (WhatsApp Commerce) ---
  'headings.sales_channels': 'Sales channels',
  'pages.online_store.label': 'Online store',
  'pages.whatsapp_channel.label': 'WhatsApp Commerce',
  'pages.whatsapp_channel.overview.label': 'Overview',
  'pages.whatsapp_channel.connect.label': 'Connect',
  'pages.whatsapp_channel.catalog.label': 'Catalog',
  'pages.whatsapp_channel.inbox.label': 'Inbox',
  'pages.whatsapp_channel.orders.label': 'Orders',
  'pages.whatsapp_channel.automations.label': 'Automations',
  'pages.whatsapp_channel.templates.label': 'Templates',
  'pages.whatsapp_channel.analytics.label': 'Analytics',
  'pages.whatsapp_channel.settings.label': 'Settings',
  'pages.whatsapp_channel.campaigns.label': 'Campaigns',
  'pages.whatsapp_channel.sections.connect_a_number.label': 'Connect a number',
  'pages.whatsapp_channel.sections.connect_a_number.description':
    'Link your WhatsApp Business number through Meta',
  'pages.whatsapp_channel.sections.sync_catalog.label': 'Sync the catalog',
  'pages.whatsapp_channel.sections.sync_catalog.description':
    'Publish your products to WhatsApp so shoppers can order in chat',
};

export default nav;
